import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { worldToLatLng } from './latLonToVec3';

const TILE_URL = (z, x, y) => `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;
const D2R = Math.PI / 180;
const R = 1.0009;
const loader = new THREE.TextureLoader();
loader.setCrossOrigin('anonymous');

const tileLat = (y, n) => Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) / D2R;
const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * D2R) / 2));

function Tile({ z, x, y }) {
  const [tex, setTex] = useState(null);
  const mat = useRef();
  const geometry = useMemo(() => {
    const n = 2 ** z;
    const lon0 = (x / n) * 360 - 180;
    const lon1 = ((x + 1) / n) * 360 - 180;
    const latT = tileLat(y, n);
    const latB = tileLat(y + 1, n);
    const g = new THREE.SphereGeometry(R, 12, 12, (lon0 + 180) * D2R, (lon1 - lon0) * D2R, (90 - latT) * D2R, (latT - latB) * D2R);
    const uv = g.attributes.uv;
    const pos = g.attributes.position;
    const mT = mercY(latT);
    const mB = mercY(latB);
    for (let i = 0; i < uv.count; i++) {
      const lat = 90 - Math.acos(Math.max(-1, Math.min(1, pos.getY(i) / R))) / D2R;
      uv.setY(i, (mercY(lat) - mB) / (mT - mB));
    }
    return g;
  }, [z, x, y]);

  useEffect(() => {
    let alive = true;
    let loaded = null;
    loader.load(TILE_URL(z, x, y), (t) => {
      loaded = t;
      if (!alive) { t.dispose(); return; }
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 4;
      setTex(t);
    });
    return () => { alive = false; loaded?.dispose(); geometry.dispose(); };
  }, [z, x, y, geometry]);

  useFrame((_, dt) => {
    if (mat.current && mat.current.opacity < 1) mat.current.opacity = Math.min(1, mat.current.opacity + dt * 3);
  });

  if (!tex) return null;
  return (
    <mesh geometry={geometry}>
      <meshPhongMaterial ref={mat} map={tex} transparent opacity={0} shininess={3} specular="#111111"
                         polygonOffset polygonOffsetFactor={-2} />
    </mesh>
  );
}

// Tile satellitari ad alta risoluzione (Esri World Imagery) caricate attorno al punto osservato.
export default function SatelliteTiles({ cols = 6, rows = 4 }) {
  const [view, setView] = useState(null);
  const last = useRef(0);

  useFrame(({ camera }) => {
    const now = performance.now();
    if (now - last.current < 250) return;
    last.current = now;
    const h = camera.position.length() - 1;
    if (h > 1.7) { if (view) setView(null); return; }
    const z = Math.max(4, Math.min(14, Math.round(Math.log2(360 / (17 * h)))));
    const { lat, lng } = worldToLatLng(camera.position);
    const n = 2 ** z;
    const latR = Math.max(-85, Math.min(85, lat)) * D2R;
    const cx = Math.floor(((lng + 180) / 360) * n);
    const cy = Math.floor(((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * n);
    if (!view || view.z !== z || view.cx !== cx || view.cy !== cy) setView({ z, cx, cy });
  });

  if (!view) return null;
  const n = 2 ** view.z;
  const tiles = [];
  for (let dx = -Math.floor(cols / 2); dx < Math.ceil(cols / 2); dx++) {
    for (let dy = -Math.floor(rows / 2); dy < Math.ceil(rows / 2); dy++) {
      const y = view.cy + dy;
      if (y < 0 || y >= n) continue;
      tiles.push({ z: view.z, x: (((view.cx + dx) % n) + n) % n, y });
    }
  }
  return <>{tiles.map(t => <Tile key={`${t.z}/${t.x}/${t.y}`} {...t} />)}</>;
}
